// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { HasStepperContext, StepperContext, withStepperContext } from "@steiner/App/Widgets/Stepper/StepperContext";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { useContext, useState } from "react";

interface OwnProps {
    extendedStyles?: StepperStyles;
    allowMovingForward?: boolean;
}

type Props = OwnProps & HasStepperContext;

export interface StepperStyles {
    container?: InjectableCss;
    stepWrapper?: InjectableCss;
    stepNumber?: TypographyProps;
    stepLabel?: TypographyProps;
    stepSeparator?: InjectableCss;
}

export const stepperStyles: StepperStyles = {};

export const Stepper: React.FC<Props> = props => {
    const { steps, currentStep, allowStepClick, moveToStepHandler } = useContext(StepperContext);
    const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set());
    const prevStepRef = React.useRef(currentStep);

    React.useEffect(() => {
        if (prevStepRef.current !== currentStep) {
            setVisitedSteps(prev => new Set(prev).add(prevStepRef.current));
            prevStepRef.current = currentStep;
        }
    }, [currentStep]);

    const [styles] = useState(() => mergeWithCss(stepperStyles, props.extendedStyles));
    const StepperComponent = () => {
        const moveNext = (index: number) => {
            document.querySelector<HTMLButtonElement>(`.stepperNextButton-${index}`)?.click();
        };

        return (
            <StyledWrapper {...styles.container} className="stepper" aria-label="Stepper">
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isCompletedSteps = visitedSteps.has(item.index);
                    const isActive = item.index === currentStep;
                    const isLastStep = steps.length - 1 === index;
                    const stepStatus = isActive
                        ? "active"
                        : isCompleted
                        ? "complete"
                        : isCompletedSteps
                        ? "visitedStep"
                        : "incomplete";
                    return (
                        <React.Fragment key={item.index}>
                            <StyledWrapper
                                {...styles.stepWrapper}
                                data-clickable={`${
                                    isActive ||
                                    isCompleted ||
                                    isCompletedSteps ||
                                    (props.allowMovingForward && item.index === currentStep + 1)
                                }`}
                                className="step-wrapper"
                                data-status={stepStatus}
                                onClick={() =>
                                    props.allowMovingForward && item.index > currentStep
                                        ? moveNext(item.index - 1)
                                        : isCompleted && moveToStepHandler(item.index)
                                }
                            >
                                <Typography {...styles.stepNumber} data-status={stepStatus} className="step-number">
                                    {item.index}
                                </Typography>
                                <Typography {...styles.stepLabel} data-status={stepStatus} className="step-label">
                                    {item.displayName || item.name}
                                </Typography>
                            </StyledWrapper>
                            {!isLastStep && (
                                <StyledWrapper
                                    role="separator"
                                    data-status={stepStatus}
                                    className="step-separator"
                                    {...styles.stepSeparator}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </StyledWrapper>
        );
    };

    return <StepperComponent />;
};

export const StepperComponent = withStepperContext(Stepper);

const widgetModule: WidgetModule = {
    component: StepperComponent,
    definition: {
        group: XNGAGE_ELEMENTS,
        displayName: "Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
